/**
 * Guide Support Service
 *
 * Frontend service for interacting with the selected guide's AI support.
 * Calls the Knowledge Hub's askCoach callable, which fetches RAG context
 * from the Knowledge Hub and calls Claude with the character's personality.
 *
 * Australian English throughout.
 */

import { auth, app } from './config/firebase';
import coachingService from './CoachingService';
import { cleanUserContext } from '../utils/companionContext';

// Cloud Function URL - will need to be deployed
// The guides run on the DDD Knowledge Hub, like every other DDD coaching
// surface - one RAG pipeline, one character registry, one set of safeguards.
//
// This used to call a per-app `guideSupportChat` function. That function is
// OFFLINE (its deploy never succeeded) and the default URL here was missing
// the project's `ddd-` prefix, so the guide chat had two independent reasons
// not to work. The Hub path is the DDD standard; the per-app duplicate is
// being retired rather than repaired.
const HUB_ASK_COACH = 'https://australia-southeast1-ddd-knowledge-hub.cloudfunctions.net/askCoach';
const APP_ID = 'friendships-vibe';

// The app's guide ids are bare ('sage'); the Hub namespaces them per app so
// two apps can both have a 'sage' without colliding.
export const hubCharacterId = (guideId) => `fv_${guideId || 'sage'}`;

// The Hub session this chat thread belongs to. The Hub opens a session when
// askCoach arrives without one, and summarises a session when the user's next
// one starts; with no id sent back, every message opened a session of its
// own, so each reply would have been summarised alone. A thread keeps its
// session while it continues (there is history) with the same guide and the
// same person; an empty history is a new thread and starts a new session.
let thread = { sessionId: null, guideId: null, uid: null };

/**
 * Navigation map for guide's action suggestions
 */
export const NAVIGATION_MAP = {
  'modules': '/modules',
  'dashboard': '/dashboard',
  'assessment': '/assessment',
  'friend circles': '/friend-circles',
  'tools': '/tools',
  'resources': '/resources',
  'life design': '/life-design',
  'privacy': '/data-privacy',
  'home': '/'
};

/**
 * Page-specific suggested questions for each guide
 */
const PAGE_QUESTIONS = {
  '/': {
    default: [
      "What can you help me with?",
      "How do I get started?",
      "What are friendship modules?"
    ]
  },
  '/dashboard': {
    default: [
      "What should I work on next?",
      "How do I improve my friendship skills?",
      "What do my results mean?"
    ]
  },
  '/modules': {
    default: [
      "Which module should I start with?",
      "How long do modules take?",
      "What will I learn about friendships?"
    ]
  },
  '/friend-circles': {
    default: [
      "What are Friend Circles?",
      "How do I create a circle?",
      "Can I join someone else's circle?"
    ]
  },
  '/tools': {
    default: [
      "What tools are available?",
      "How do I use the conversation starters?",
      "What's the friendship check-in for?"
    ]
  },
  '/assessment': {
    default: [
      "What does the assessment measure?",
      "How long does it take?",
      "Will my answers be private?"
    ]
  }
};

/**
 * Character-specific question variations
 */
const CHARACTER_QUESTIONS = {
  sage: {
    greeting: "What wisdom can you share today?",
    help: "How should I approach this thoughtfully?"
  },
  jaz: {
    greeting: "What's good, Jaz?",
    help: "Can you hype me up for this?"
  },
  mika: {
    greeting: "What's something interesting you've noticed?",
    help: "Can you explain how this works?"
  },
  riley: {
    greeting: "What's on your mind today, Riley?",
    help: "How do you see the bigger picture here?"
  },
  alex: {
    greeting: "Hey Alex, how's it going?",
    help: "Have you been through something like this?"
  },
  sam: {
    greeting: "How are you feeling, Sam?",
    help: "Can you help me understand my feelings about this?"
  }
};

class GuideSupportService {
  /**
   * Ask the selected guide a question via the support chat function
   *
   * @param {string} question - User's question
   * @param {string} guideId - Selected guide character ID
   * @param {Array} conversationHistory - Previous messages in the session
   * @param {Object} context - Current app context (module, page, etc.)
   * @returns {Promise<Object>} - Guide's response
   */
  static async askGuide(question, guideId, conversationHistory = [], context = {}) {
    try {
      if (!auth?.currentUser) {
        return {
          success: true,
          response: this.getSignInPrompt(guideId),
          actions: [{ type: 'navigate', path: '/signin' }],
          isSignInPrompt: true
        };
      }

      const { getFunctions, httpsCallableFromURL } = await import('firebase/functions');
      const callable = httpsCallableFromURL(
        getFunctions(app, 'australia-southeast1'),
        HUB_ASK_COACH
      );

      const { uid } = auth.currentUser;
      const continuing = conversationHistory.length > 0 && thread.sessionId
        && thread.guideId === guideId && thread.uid === uid;

      const res = (await callable({
        question,
        appId: APP_ID,
        ...(continuing ? { sessionId: thread.sessionId } : {}),
        // Which of the six voices the user picked. The Hub validates this
        // against that app's own characters and falls back to the app
        // default if it is unknown, so a stale id degrades quietly.
        characterId: hubCharacterId(guideId),
        conversationHistory,
        // The page, and the module the reader has open, if any.
        userContext: cleanUserContext(context)
      })).data;

      if (!res?.success) {
        if (res?.error === 'rate-limited' || res?.error === 'daily-limit') {
          return { success: false, response: res.response || this.getRateLimitMessage(guideId), isRateLimited: true };
        }
        if (res?.error === 'signin-required') {
          return { success: false, response: this.getAuthErrorMessage(guideId) };
        }
        if (res?.error === 'consent-required') {
          return { success: false, error: 'consent-required', response: this.getConsentMessage(guideId) };
        }
        return { success: false, response: res?.response || this.getErrorMessage(guideId) };
      }

      if (res.sessionId) thread = { sessionId: res.sessionId, guideId, uid };

      return {
        success: true,
        response: res.response || this.getFallbackResponse(guideId),
        actions: res.actions || []
      };
    } catch (error) {
      console.error('[GuideSupportService] Error:', error);

      const code = error?.code || '';
      if (code.includes('unauthenticated')) {
        return { success: false, response: this.getAuthErrorMessage(guideId) };
      }
      // The Hub will not coach anyone whose coaching profile has no consent on
      // it, and says so with failed-precondition. That is a setup step, not a
      // fault, so it comes back as its own result and the modal shows the same
      // consent gate the Coaching page shows - instead of "something went wrong".
      if (code.includes('failed-precondition')) {
        return { success: false, error: 'consent-required', response: this.getConsentMessage(guideId) };
      }
      if (code.includes('resource-exhausted')) {
        return { success: false, response: this.getRateLimitMessage(guideId), isRateLimited: true };
      }
      return {
        success: false,
        response: this.getErrorMessage(guideId)
      };
    }
  }

  /**
   * Whether the signed-in user's coaching profile carries granted consent.
   *
   * The six guides and the Coaching page are one coach behind two doors: the
   * same Hub function, the same profile, the same consent record, the same
   * daily allowance. The Coaching page has always asked for consent before the
   * first message; the guide modal did not, so the Hub refused every first
   * message with failed-precondition and the modal blamed the network. The
   * modal now asks the question the Coaching page asks, before the first send.
   *
   * @returns {Promise<{granted: boolean, profile: Object|null, error?: string}>}
   *   `error` is set when the profile could not be READ (signed out, offline,
   *   hub hiccup) - which is not the same as consent being absent.
   */
  static async getConsentStatus() {
    const res = await coachingService.getProfile();
    if (!res?.success) {
      return { granted: false, profile: null, error: res?.error || 'unknown' };
    }
    return { granted: !!res.profile?.consent?.granted, profile: res.profile || null };
  }

  /**
   * Record consent on the shared coaching profile - the same call the Coaching
   * page's gate makes, so either door unlocks both.
   */
  static grantConsent(opts) {
    return coachingService.grantConsent(opts);
  }

  /**
   * Consent-needed message in character voice. Shown only if the Hub refuses
   * mid-conversation (a profile deleted from another tab, say); on a fresh
   * open the gate itself does the talking.
   */
  static getConsentMessage(guideId) {
    const messages = {
      sage: "Before we go further there's a short setup step - what I remember, and how your words are handled. About a minute.",
      jaz: "One quick setup screen before we get into it - what I remember and how this works. Then we're on.",
      mika: "Quick setup step first: a short screen on what I remember and where your messages go. Then we can dig in.",
      riley: "Before we begin, a short setup step - what I hold onto, and how your words are handled. Then we can talk properly.",
      alex: "One thing first - a quick setup screen so you know what I remember and how this works. Takes a minute, then we're good.",
      sam: "Before we start, a short setup step so you know what I remember and how your words are looked after. Then I'm all yours."
    };
    return messages[guideId] || messages.sage;
  }

  /**
   * Get sign-in prompt in character voice
   */
  static getSignInPrompt(guideId) {
    const prompts = {
      sage: "That's a thoughtful question. My full ability to help you — drawing on everything I know about friendships and tailoring advice to your situation — becomes available when you sign in.\n\nSigning in is free, and it means your progress is saved across devices. You can sign in from the menu above. I'll be here when you're ready.",
      jaz: "Love that you're curious. I can help you a lot better once you sign in — it's free, takes about thirty seconds, and then I can actually remember our conversations and give you advice that fits you.\n\nThe sign in button is up top. Then let's get into it. 🙌",
      mika: "Great question! My full capabilities unlock when you sign in. Think of it like... I'm a beta version right now, but signing in gets you the full release.\n\nIt's free, and then I can remember context and give you properly tailored advice. Pretty useful, right?",
      riley: "I appreciate you reaching out. My ability to truly help you deepens when you sign in.\n\nIt's a simple, free process that allows our conversations to have continuity, and for me to understand your unique journey. When you're ready, the sign in option is above.",
      alex: "Oh hey, good question! But I can't really give you proper personalised help unless you sign in first.\n\nIt's free and takes no time. Then I can actually remember our chats and give you advice that actually fits your situation. Trust me, it's worth it.",
      sam: "I hear you, and I want to help. I can support you much better once you sign in.\n\nIt's free and safe, and it means I can remember our conversations and really understand what you're going through. Your feelings and progress will be safely stored. Would you like to sign in?"
    };
    return prompts[guideId] || prompts.sage;
  }

  /**
   * Get rate limit message in character voice
   */
  static getRateLimitMessage(guideId) {
    const messages = {
      sage: "I need a moment to gather my thoughts. We've been chatting quite a bit — could you give me a minute before your next question?",
      jaz: "Whoa, we're really going at it! I need a quick breather. Give me a minute and then we can keep vibing! 💫",
      mika: "Interesting situation — we've hit a temporary limit. It's like when a server needs to cool down. Try again in a minute?",
      riley: "Let's pause here for a moment. Sometimes space helps us process. I'll be ready to continue shortly.",
      alex: "Ha, we've been going hard! I need a sec to catch up. Maybe grab a drink and come back in a minute?",
      sam: "I feel like we both might need a gentle pause. Take a breath, and I'll be here when you're ready to continue."
    };
    return messages[guideId] || messages.sage;
  }

  /**
   * Get auth error message in character voice
   */
  static getAuthErrorMessage(guideId) {
    const messages = {
      sage: "It seems there's been a hiccup with your session. Could you sign in again? I'll be here waiting.",
      jaz: "Oops! Looks like we got disconnected somehow. Quick sign in and we're back in business! 💪",
      mika: "Hmm, authentication error detected. A quick sign-out and sign-in should resolve this. Classic tech fix!",
      riley: "Something's shifted with your connection. Signing in again should restore our conversation flow.",
      alex: "Ah, session expired I think. Happens to everyone. Quick sign in and we're good to go again!",
      sam: "Oh, it looks like your session needs refreshing. Would you mind signing in again? I'm here when you're ready."
    };
    return messages[guideId] || messages.sage;
  }

  /**
   * Get generic error message in character voice
   */
  static getErrorMessage(guideId) {
    const messages = {
      sage: "I'm experiencing some difficulty at the moment. Perhaps try again in a moment? These things often resolve themselves.",
      jaz: "Ugh, something went sideways! Not ideal but these things happen. Try again in a sec? 🙏",
      mika: "Error encountered! Don't worry, it's probably temporary. Give it another shot in a moment?",
      riley: "Something seems off in the flow right now. These moments pass. Would you try again shortly?",
      alex: "Oops, hit a snag there. My bad. Maybe try that again in a sec?",
      sam: "I'm so sorry, something's not quite right. Please try again — I really want to help you."
    };
    return messages[guideId] || messages.sage;
  }

  /**
   * Get fallback response in character voice
   */
  static getFallbackResponse(guideId) {
    const responses = {
      sage: "That's an interesting question. Let me consider this more carefully for you.",
      jaz: "Ooh, good one! Let me think about that for you!",
      mika: "Fascinating question! I'm processing that for you.",
      riley: "I'm reflecting on that. Let me share my thoughts.",
      alex: "Hmm, let me think about that one for you.",
      sam: "I'm here with you. Let me gather my thoughts on that."
    };
    return responses[guideId] || responses.sage;
  }

  /**
   * Get suggested questions based on current page and guide
   *
   * @param {string} guideId - Selected guide character ID
   * @param {string} currentPage - Current route path
   * @returns {Array<string>} - Suggested questions
   */
  static getSuggestedQuestions(guideId, currentPage = '/') {
    // Get page-specific questions
    const pageConfig = PAGE_QUESTIONS[currentPage] || PAGE_QUESTIONS['/'];
    const baseQuestions = pageConfig.default || [];

    // Get character-specific variations
    const charQuestions = CHARACTER_QUESTIONS[guideId] || CHARACTER_QUESTIONS.sage;

    // Mix in a character-specific question
    const suggestions = [
      charQuestions.greeting,
      ...baseQuestions.slice(0, 2)
    ];

    return suggestions.slice(0, 4);
  }

  /**
   * Parse navigation actions from response
   *
   * @param {Array} actions - Actions from response
   * @returns {Array} - Parsed navigation targets
   */
  static parseNavigationActions(actions = []) {
    return actions
      .filter(action => action.type === 'navigate')
      .map(action => ({
        path: action.path,
        label: action.label || 'Go there'
      }));
  }
}

export default GuideSupportService;
